import React from "react";
import { useKaraoke } from "../../store/KaraokeStore";
import { Play, X, ChevronUp, ChevronDown, Trash2, GripVertical, User, RotateCcw } from "lucide-react";

const NowPlayingBadge = ({ isPlaying }) => (
  <div className="flex items-center gap-1.5 shrink-0 text-secondary" data-testid="queue-now-playing-indicator">
    <div className={`flex items-end gap-[2px] h-3 ${isPlaying ? "" : "opacity-50 [&>span]:[animation-play-state:paused]"}`}>
      <span className="eq-bar !w-[2px] !h-3" />
      <span className="eq-bar !w-[2px] !h-3" />
      <span className="eq-bar !w-[2px] !h-3" />
    </div>
    <span className="text-[9px] font-mono uppercase tracking-wider">{isPlaying ? "En curso" : "Pausada"}</span>
  </div>
);

const SongInfo = ({ song, isPlayed = false }) => (
  <div className="flex-1 min-w-0">
    <p
      className={`text-sm font-semibold leading-snug line-clamp-2 break-words ${isPlayed ? "line-through text-muted-foreground" : ""}`}
      title={song.title}
    >
      {song.title}
    </p>
    <p className="text-xs mt-1 flex items-center gap-1 min-w-0">
      <User className="w-3 h-3 shrink-0 text-secondary" />
      <span className="truncate font-semibold text-secondary">{song.singer || "Invitado"}</span>
      {song.duration && <span className="text-muted-foreground font-mono shrink-0">· {song.duration}</span>}
      {isPlayed && (
        <span className="ml-1 text-[9px] font-mono uppercase tracking-wider text-muted-foreground/80 border border-border rounded px-1 py-px shrink-0">
          Reproducida
        </span>
      )}
    </p>
    {song.dedication && (
      <p className="text-[10px] text-secondary/80 italic mt-0.5 truncate" title={song.dedication}>
        ♥ &ldquo;{song.dedication}&rdquo;
      </p>
    )}
  </div>
);

const Thumb = ({ src, isPlayed }) => (
  <img src={src} alt="" className={`w-14 h-9 object-cover rounded-sm shrink-0 ${isPlayed ? "grayscale" : ""}`} />
);

const SectionLabel = ({ children, testId }) => (
  <p className="px-3 pt-3 pb-1.5 text-[10px] font-mono tracking-[0.25em] uppercase text-muted-foreground" data-testid={testId}>
    {children}
  </p>
);

const QueuePanel = () => {
  const { queue, currentIndex, currentSong, playNowSong, isPlaying, playIndex, removeFromQueue, moveItem, clearQueue, togglePlayed } = useKaraoke();
  const currentQueueIdx = playNowSong ? -1 : currentIndex;
  const pendingCount = queue.filter((s, i) => i !== currentQueueIdx && !s.played).length;
  const others = queue.map((song, idx) => ({ song, idx })).filter(({ idx }) => idx !== currentQueueIdx);

  return (
    <section
      className="col-span-1 flex flex-col h-full border border-border bg-surface rounded-lg overflow-hidden"
      data-testid="queue-panel"
    >
      <div className="p-4 border-b border-border shrink-0 flex items-center justify-between">
        <div>
          <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-muted-foreground">/// Cola</p>
          <h2 className="font-display text-lg font-bold mt-1">
            Próximas <span className="text-muted-foreground font-mono text-sm" data-testid="queue-pending-count">({pendingCount})</span>
          </h2>
        </div>
        {queue.length > 0 && (
          <button
            onClick={clearQueue}
            data-testid="clear-queue-button"
            className="text-xs text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1"
            title="Vaciar cola"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Limpiar
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {currentSong && (
          <div className="border-b border-border" data-testid="queue-now-playing">
            <SectionLabel>En curso</SectionLabel>
            <div className="px-3 pb-3 pl-2 flex items-center gap-3 border-l-4 border-l-primary bg-primary/10 ml-0 pt-2">
              <Thumb src={currentSong.thumbnail} />
              <SongInfo song={currentSong} />
              <NowPlayingBadge isPlaying={isPlaying} />
            </div>
          </div>
        )}

        {others.length === 0 ? (
          !currentSong && (
            <div className="h-full flex flex-col items-center justify-center text-center p-6 text-muted-foreground">
              <GripVertical className="w-8 h-8 mb-2 opacity-30" />
              <p className="text-sm">La cola está vacía</p>
              <p className="text-xs mt-1 opacity-60">Agrega canciones desde el buscador</p>
            </div>
          )
        ) : (
          <>
            {currentSong && <SectionLabel testId="queue-pending-label">Próximas ({pendingCount})</SectionLabel>}
            {others.map(({ song, idx }) => {
              const isPlayed = !!song.played;
              return (
                <div
                  key={song.id}
                  data-testid={`queue-item-${idx}`}
                  className={`relative border-b border-border last:border-0 transition-all p-3 flex items-center gap-3 group ${
                    isPlayed ? "opacity-45" : "hover:bg-white/5"
                  }`}
                >
                  <div className="text-xs font-mono text-muted-foreground w-6 shrink-0 text-center">
                    {String(idx + 1).padStart(2, "0")}
                  </div>

                  <Thumb src={song.thumbnail} isPlayed={isPlayed} />
                  <SongInfo song={song} isPlayed={isPlayed} />

                  <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => moveItem(song.id, "up")}
                      disabled={idx === 0}
                      data-testid={`move-up-queue-item-${idx}`}
                      className="w-6 h-5 rounded hover:bg-white/10 flex items-center justify-center disabled:opacity-20"
                      title="Subir"
                    >
                      <ChevronUp className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => moveItem(song.id, "down")}
                      disabled={idx === queue.length - 1}
                      data-testid={`move-down-queue-item-${idx}`}
                      className="w-6 h-5 rounded hover:bg-white/10 flex items-center justify-center disabled:opacity-20"
                      title="Bajar"
                    >
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </div>

                  {isPlayed ? (
                    <button
                      onClick={() => togglePlayed(song.id)}
                      data-testid={`enable-queue-item-${idx}`}
                      className="w-7 h-7 rounded-full bg-secondary/15 hover:bg-secondary text-secondary hover:text-secondary-foreground flex items-center justify-center shrink-0 transition-colors"
                      title="Habilitar de nuevo"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <button
                      onClick={() => playIndex(idx)}
                      data-testid={`play-queue-item-${idx}`}
                      className="w-7 h-7 rounded-full bg-primary/15 hover:bg-primary text-primary hover:text-primary-foreground flex items-center justify-center shrink-0 transition-colors"
                      title="Reproducir ahora"
                    >
                      <Play className="w-3 h-3 ml-0.5" fill="currentColor" />
                    </button>
                  )}
                  <button
                    onClick={() => removeFromQueue(song.id)}
                    data-testid={`remove-queue-item-${idx}`}
                    className="w-7 h-7 rounded-full hover:bg-destructive/20 hover:text-destructive flex items-center justify-center shrink-0 transition-colors opacity-60 hover:opacity-100"
                    title="Quitar"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </>
        )}
      </div>
    </section>
  );
};

export default QueuePanel;
